import { ConsoleInterceptor } from "../services/ConsoleInterceptor";

type DiagnosticsConsole = Pick<
    Console,
    "info" | "warn" | "debug" | "log" | "group" | "groupCollapsed" | "groupEnd" | "table"
>;

/** Prefix used to filter and skip backend log forwarding. */
export const MEMORY_DIAGNOSTICS_LOG_PREFIX = "[AppMemory]";

/**
 * Returns the console surface that bypasses {@link ConsoleInterceptor} in dev.
 *
 * Patched methods (log/info/warn/debug) use saved originals to skip backend forwarding.
 * Unpatched methods (table, group*) use the global console — they are never intercepted.
 *
 * @returns Console bindings safe for local-only memory diagnostics.
 */
function getLocalDiagnosticsConsole(): DiagnosticsConsole {
    if ( typeof __IS_DEV__ !== "undefined" && __IS_DEV__ && ConsoleInterceptor.isActive() ) {
        const original = ConsoleInterceptor.getOriginalConsole();

        return {
            "log":            original.log,
            "info":           original.info,
            "warn":           original.warn,
            "debug":          original.debug,
            "table":          console.table.bind( console ),
            "group":          console.group.bind( console ),
            "groupCollapsed": console.groupCollapsed.bind( console ),
            "groupEnd":       console.groupEnd.bind( console ),
        };
    }

    return console;
}

/**
 * Logs an informational memory diagnostics line locally (never forwarded to `/api/logs`).
 *
 * @param message Primary log line (should start with {@link MEMORY_DIAGNOSTICS_LOG_PREFIX}).
 * @param data Optional structured payload.
 * @returns Nothing.
 */
export function memoryDiagnosticsInfo( message: string, ...data: unknown[] ): void {
    getLocalDiagnosticsConsole().info( message, ...data );
}

/**
 * Logs a memory diagnostics warning locally.
 *
 * @param message Warning line.
 * @param data Optional structured payload.
 * @returns Nothing.
 */
export function memoryDiagnosticsWarn( message: string, ...data: unknown[] ): void {
    getLocalDiagnosticsConsole().warn( message, ...data );
}

/**
 * Logs a memory diagnostics debug payload locally.
 *
 * @param message Debug label.
 * @param data Optional structured payload.
 * @returns Nothing.
 */
export function memoryDiagnosticsDebug( message: string, ...data: unknown[] ): void {
    getLocalDiagnosticsConsole().debug( message, ...data );
}

/**
 * Opens a collapsed diagnostics group in the local console.
 *
 * @param label Group title.
 * @returns Nothing.
 */
export function memoryDiagnosticsGroupCollapsed( label: string ): void {
    getLocalDiagnosticsConsole().groupCollapsed( label );
}

/**
 * Closes the current diagnostics console group.
 *
 * @returns Nothing.
 */
export function memoryDiagnosticsGroupEnd(): void {
    getLocalDiagnosticsConsole().groupEnd();
}

/**
 * Prints a diagnostics table to the local console inside a labeled group.
 *
 * @param label Collapsed group title (should include {@link MEMORY_DIAGNOSTICS_LOG_PREFIX}).
 * @param data Tabular data for DevTools.
 * @returns Nothing.
 */
export function memoryDiagnosticsTable( label: string, data: unknown ): void {
    const localConsole = getLocalDiagnosticsConsole();
    localConsole.groupCollapsed( label );
    localConsole.table( data );
    localConsole.groupEnd();
}

/**
 * Returns whether a forwarded console message should be skipped by {@link ConsoleInterceptor}.
 *
 * @param message First argument passed to a patched console method.
 * @returns True when the message is a memory diagnostics line.
 */
export function shouldSkipMemoryDiagnosticsForwarding( message: unknown ): boolean {
    return typeof message === "string" &&
      ( message.startsWith( MEMORY_DIAGNOSTICS_LOG_PREFIX ) ||
        message.startsWith( "[MemoryDiagnostics]" ) );
}
